import React, { useCallback, useMemo, useState } from "react";
import { Box, Card, CardContent, Divider, IconButton, Typography } from "@mui/material";
import { PostType } from "../../types/types";
import { getRelativeTime } from "../../hooks/getRelativeTime";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import BadgeIcon from "@mui/icons-material/Badge";
import WorkIcon from "@mui/icons-material/Work";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import CurrencyYenIcon from "@mui/icons-material/CurrencyYen";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import DeleteIcon from '@mui/icons-material/Delete';
import theme from "../../utils/theme";
import { Link, useLocation } from "react-router-dom";
import AlertDialog from "../../components/AlertDialog";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { addToFavorites, removeFromFavorites } from "../../slices/favoriteSlice";
import { usePostRequest } from "../../hooks/postRequest";
import FavoriteIcon from '@mui/icons-material/Favorite';
import { ADMIN_BASE_URL } from "../../utils/config";
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface PostCardProps {
  index: number;
  post: PostType;
  fetchData?: () => void;
}

const PostCard: React.FC<PostCardProps> = ({ index, post, fetchData }) => {
  const location = useLocation();
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const dispatch = useAppDispatch();
  const { favIds } = useAppSelector(state => state.favIds);
  const postRequest = usePostRequest();
  const { ref, inView } = useInView();

  const handleDelete = useCallback((event: React.MouseEvent<HTMLButtonElement>, id: number) => {
    event.stopPropagation();
    event.preventDefault();
    setDeleteId(id);
    setShowAlert(true);
  }, []);

  const handleFavClick = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
    event.preventDefault();
    const prevIds = window.localStorage.getItem('favIds');
    if (prevIds) {
      const parsedIds = JSON.parse(prevIds);
      if (parsedIds.includes(id)) {
        dispatch(removeFromFavorites(id));
      } else {
        dispatch(addToFavorites(id));
      }
    } else {
      console.log('No favIds found in localStorage');
    }
  };

  const handleConfirmDelete = useCallback(async () => {
    if (deleteId !== null) {
      try {
        await postRequest({ baseUrl: ADMIN_BASE_URL, method: 'DELETE', data: null, id: deleteId, perPage: 12 });
        if (fetchData) {
          fetchData();
        }
        setShowAlert(false);
      } catch (error) {
        console.error('Failed to delete post:', error);
      }
    }
  }, [deleteId, fetchData, postRequest]);

  const handleCancelDelete = () => {
    setShowAlert(false);
    setDeleteId(null);
  };

  const isExpired = new Date(post.deadLine) < new Date();

  const postLink = useMemo(() => {
    if (location.pathname === '/' || location.pathname.includes('/jobs')) {
      return `/jobs/${post.id}`;
    }
    return `${post.id}`;
  }, [location.pathname, post.id]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.3, delay: index * 0.2 }}
      style={{ height: '100%' }}
    >
      <Link to={postLink} style={{ textDecoration: 'none' }}>
        <Card
          elevation={3}
          className="card"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            "&:hover .arrow-icon": {
              color: theme.palette.primary.main,
              fontSize: '30px',
              transition: 'all .2s',
            },
            "&:hover": {
              backgroundColor: theme.palette.grey[200],
              transition: 'all .2s',
            },
          }}
        >
          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              {isExpired ? (
                <Typography
                  color="error"
                  sx={{ fontStyle: 'italic', fontSize: '14px', whiteSpace: 'nowrap', mr: 2 }}
                >
                  expired
                </Typography>
              ) : (
                <Typography
                  sx={{ fontStyle: 'italic', fontSize: '14px', whiteSpace: 'nowrap', mr: 2 }}
                  color="primary"
                >
                  {getRelativeTime(post.date)}
                </Typography>
              )}
              <Divider sx={{ width: "100%" }} color={isExpired ? "error" : "primary"} />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ width: '350px' }}>
                <Typography sx={{ mb: 2, fontSize: '20px' }}>
                  {post.title.length > 50 ? `${post.title.substring(0, 50)}...` : post.title}
                </Typography>
              </Box>
              <ArrowOutwardIcon className="arrow-icon" />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-end", mb: 2 }}>
                <WorkIcon color="primary" />
                <Typography sx={{ ml: 1, fontSize: { xs: '10px', sm: '12px', md: '14px' } }} variant="body2">
                  {post.category}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-end", mb: 2 }}>
                <BadgeIcon color="primary" />
                <Typography sx={{ ml: 1, fontSize: { xs: '10px', sm: '12px', md: '14px' } }}>
                  {post.visaType}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-end", mb: 2 }}>
                <LocationCityIcon color="primary" />
                <Typography sx={{ ml: 1, fontSize: { xs: '10px', sm: '12px', md: '14px' } }}>
                  {post.location}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ px: 2 }} color="text.secondary">
              <Typography
                dangerouslySetInnerHTML={{
                  __html: post.description.length > 150
                    ? `${post.description.substring(0, 150)}...`
                    : post.description,
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ fontSize: '20px' }}>Basic Salary - {Number(post.salary).toLocaleString()}</Typography>
                <CurrencyYenIcon />
              </Box>
              {location.pathname.includes('/admin') ? (
                <IconButton onClick={(event) => handleDelete(event, post.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              ) : (
                <IconButton  onClick={(event) => handleFavClick(event, post.id)} sx={{ cursor: 'pointer' }}>
                  {
                    favIds.includes(post.id) ?
                    <FavoriteIcon color="primary"/>
                    :
                    <FavoriteBorderIcon />
                  }
                </IconButton>
              )}
            </Box>
          </CardContent>
        </Card>
      </Link>
      <AlertDialog
        toggle={showAlert}
        setToggle={setShowAlert}
        cancel={handleCancelDelete}
        confirm={handleConfirmDelete}
        title="Are you sure?"
        content="You want to delete this post."
        type="error"
      />
    </motion.div>
  );
};

export default PostCard;
